import React, { useState, lazy, Suspense, useEffect } from 'react';
import type { CollectionAnalyticsDataPoint, FloorPriceTimeRange } from '../../api/analytics';
import { getCollectionAnalytics } from '../../api/analytics';
import '../styles/FloorPriceChart.css';

// Lazy load recharts to reduce initial bundle size
const LazyChart = lazy(() => import('./CollectionAnalyticsChartImpl'));

export interface CollectionAnalyticsChartProps {
  collectionId: string;
  className?: string;
}

const LoadingSkeleton = () => (
  <div className="floor-price-chart-skeleton">
    <div className="skeleton-header"></div>
    <div className="skeleton-chart"></div>
    <div className="skeleton-legend"></div>
  </div>
);

export const CollectionAnalyticsChart: React.FC<CollectionAnalyticsChartProps> = ({
  collectionId,
  className = '',
}) => {
  const [activeTimeRange, setActiveTimeRange] = useState<FloorPriceTimeRange>('30d');
  const [data, setData] = useState<CollectionAnalyticsDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const timeRangeOptions: { value: FloorPriceTimeRange; label: string }[] = [
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' },
    { value: '90d', label: '90D' },
    { value: '1y', label: '1Y' },
    { value: 'all', label: 'All' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getCollectionAnalytics(collectionId, activeTimeRange);
        setData(result.history);
      } catch (err) {
        console.error('Failed to fetch collection analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collectionId, activeTimeRange]);

  const handleTimeRangeChange = (range: FloorPriceTimeRange) => {
    setActiveTimeRange(range);
  };

  return (
    <div className={`floor-price-chart ${className}`}>
      {/* Header with time range selector */}
      <div className="floor-price-chart-header">
        <h3 className="floor-price-chart-title">Price & Volume</h3>
        <div className="floor-price-chart-time-range">
          {timeRangeOptions.map((option) => (
            <button
              key={option.value}
              className={`time-range-button ${activeTimeRange === option.value ? 'active' : ''}`}
              onClick={() => handleTimeRangeChange(option.value)}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Content */}
      <div className="floor-price-chart-content">
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="floor-price-chart-error">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="error-icon">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="error-message">{error}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="floor-price-chart-empty">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="empty-icon">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
            <p>No analytics data available for this time range</p>
            <p className="empty-hint">Data is recorded from offers and sales transactions.</p>
          </div>
        ) : (
          <Suspense fallback={<LoadingSkeleton />}>
            <LazyChart data={data} />
          </Suspense>
        )}
      </div>
    </div>
  );
};
